import { Button } from "@/components/ui/button";
import { Award, Target, TrendingUp, Mail, Calendar } from "lucide-react";
import { ScoringResult, AuditFormData } from "@/pages/AuditFlow";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface AuditResultsProps {
  result: ScoringResult;
  formData: AuditFormData;
}

export default function AuditResults({ result, formData }: AuditResultsProps) {
  const { toast } = useToast();
  const [emailSent, setEmailSent] = useState(false);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "Early":
        return "text-bronze";
      case "Automation-Ready":
        return "text-cyan";
      case "Transformation-Ready":
        return "text-green-400";
      default:
        return "text-cyan";
    }
  };

  const getTierDescription = (tier: string) => {
    switch (tier) {
      case "Early":
        return "We'll clarify use-cases and enable quick wins.";
      case "Automation-Ready":
        return "Automate targeted bottlenecks for near-term impact.";
      case "Transformation-Ready":
        return "Orchestrate multiple AI systems for compound efficiency.";
      default:
        return "";
    }
  };

  const handleEmailReport = async () => {
    // Email was already sent in the submission, this is just a confirmation
    setEmailSent(true);
    toast({
      title: "Report Sent!",
      description: "Check your inbox for your AI Transformation Snapshot.",
    });

    // Track analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "pdf_generated", {
        event_category: "engagement",
        event_label: result.tier,
      });
    }
  };

  const handleBookCall = () => {
    // Track analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "booked_call", {
        event_category: "conversion",
        event_label: result.tier,
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-center mb-12">
        <div className="inline-block px-4 py-1 rounded-full border border-bronze/30 bg-bronze/5 mb-6 text-xs text-bronze font-bold uppercase tracking-wider">
          Your Results
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
          Your AI Transformation Snapshot
        </h1>
        <p className="text-xl text-light-gray/80">
          Based on your responses, here's what we found
        </p>
      </div>

      {/* Score Display */}
      <div className="bg-dark-card rounded-2xl p-12 border border-border/20 mb-8 text-center">
        <div className="mb-6">
          <Award className="w-16 h-16 text-bronze mx-auto mb-4" />
          <div className="text-7xl font-bold text-cyan mb-2">{result.score}</div>
          <div className="text-lg text-light-gray/70">out of 100</div>
        </div>

        <div className={`inline-block px-6 py-3 rounded-full bg-bronze/10 border border-bronze/30 ${getTierColor(result.tier)}`}>
          <span className="text-2xl font-bold">{result.tier}</span>
        </div>

        <p className="text-light-gray/90 mt-6 text-lg">
          {getTierDescription(result.tier)}
        </p>
      </div>

      {/* Top Opportunities */}
      <div className="bg-dark-card rounded-2xl p-8 md:p-10 border border-border/20 mb-8">
        <div className="flex items-center gap-3 mb-6">
          <Target className="w-6 h-6 text-cyan" />
          <h2 className="text-2xl font-bold text-foreground">
            Top Opportunities for Your Business
          </h2>
        </div>
        <ul className="space-y-4">
          {result.topOpportunities.map((opportunity, index) => (
            <li
              key={index}
              className="flex items-start gap-3 p-4 bg-background rounded-lg border border-border/20"
            >
              <TrendingUp className="w-5 h-5 text-bronze mt-0.5 flex-shrink-0" />
              <span className="text-light-gray/90">{opportunity}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommended Next Step */}
      <div className="bg-dark-card rounded-2xl p-8 md:p-10 border border-border/20 mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Recommended Next Step
        </h2>
        <p className="text-light-gray/90 mb-6 text-lg leading-relaxed">
          {result.recommendation}
        </p>
        <div className="p-4 bg-cyan/5 border border-cyan/20 rounded-lg">
          <p className="text-sm text-light-gray/90">
            <strong className="text-foreground">ArqeraAI Framework:</strong>{" "}
            Strategy → Education → Development. Your results guide the path.
          </p>
        </div>
      </div>

      {/* CTAs */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Button
          asChild
          size="lg"
          className="bg-cyan hover:bg-cyan-muted text-deep-navy font-bold text-lg py-6 h-auto"
          onClick={handleBookCall}
        >
          <a
            href="https://calendly.com/hello-arqeraai/30min"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Calendar className="mr-2 w-5 h-5" />
            Book Your Strategy Audit
          </a>
        </Button>

        <Button
          onClick={handleEmailReport}
          disabled={emailSent}
          size="lg"
          variant="outline"
          className="border-bronze text-bronze hover:bg-bronze hover:text-deep-navy font-semibold text-lg py-6 h-auto"
        >
          <Mail className="mr-2 w-5 h-5" />
          {emailSent ? "Report Sent!" : "Email My Report"}
        </Button>
      </div>

      {/* Bottom Note */}
      <div className="text-center py-8 border-t border-border/20">
        <p className="text-light-gray/80 mb-2">
          Join the leaders transforming their operations with AI
        </p>
        <p className="text-sm text-medium-gray">
          <strong className="text-foreground">ArqeraAI</strong>, Architects of Intelligent Systems
        </p>
      </div>
    </div>
  );
}